import { useState, useMemo, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  SearchCandidateFilterTypes,
  SearchCandidateInput,
  useSearchCandidateQuery,
} from '$store/modules/candidates';
import Spinner from '$components/Spinner/Spinner';
import { GroupedCountries } from '$constants/countries';
import ModalInTailwind from '$components/ModalInTailwind/ModalInTailwind';
import SearchCandidateAddToPositionModal from '$components/SearchCandidates/SearchCandidateAddToPositionModal/SearchCandidateAddToPositionModal';
import SearchCandidatesTable from '$components/SearchCandidates/SearchCandidatesTable/SearchCandidatesTable';
import SearchCandidatesTableNavigation from '$components/SearchCandidates/SearchCandidatesTableNavigation/SearchCandidatesTableNavigation';
import MultiFilterChipsInput from '$components/Internal/Positions/Filters/MultiFIlterChipsInput/MultiFilterChipsInput';

import SearchCandidatesFilter from './SearchCandidatesFilter/SearchCandidatesFilter';

type IColumnSort = {
  column: 'id';
  sortType: 'ASC' | 'DESC';
};

const SearchCandidates = () => {
  const filterOptions = [
    {
      title: SearchCandidateFilterTypes.AllFields,
      bgClassName: 'bg-internal-main',
    },
    {
      title: SearchCandidateFilterTypes.Skills,
      bgClassName: 'bg-internal-link',
    },
    {
      title: SearchCandidateFilterTypes.JobTitle,
      bgClassName: 'bg-indigo-600',
    },
  ];

  const [openAddToPosition, setOpenAddToPosition] = useState(false);
  const [rowSelection, setRowSelection] = useState<{ number: boolean } | {}>(
    {}
  );
  const lastSearch = useRef('');
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(
    parseInt(searchParams.get('page') || '1', 10)
  );
  const [pageSize, setPageSize] = useState(
    parseInt(searchParams.get('take') || '100', 10)
  );
  const [columnSort, setColumnSort] = useState<IColumnSort>({
    column: 'id',
    sortType: 'DESC',
  });
  const [isGeneratingCSV, setIsGeneratingCSV] = useState(false);

  const searchCandidatesFilters = useMemo(() => {
    const searchParamsJSON = Object.fromEntries(searchParams.entries());

    const duplicatedKeys = ['page', 'take']; // this fields are already passed through

    const UserQuery: Record<string, string[]> = {};
    Array.from(searchParams.entries()).forEach(([key, value]) => {
      if (!UserQuery[key] && duplicatedKeys.indexOf(key.toLowerCase()) === -1) {
        UserQuery[key] = [];
      }

      if (UserQuery[key]) UserQuery[key].push(value);
    });

    const parsedCountryParams = searchParamsJSON?.Country?.split(',').reduce(
      (prev, act) => {
        const isGroupedCountry =
          Object.keys(GroupedCountries).indexOf(act) >= 0;
        if (isGroupedCountry) {
          prev.CountryGroup.push(act);
        } else {
          prev.Country.push(act);
        }
        return prev;
      },
      { CountryGroup: [] as string[], Country: [] as string[] }
    );

    const parsedTenantParam = searchParamsJSON?.Tenant?.split(',').reduce(
      (prev, act) => {
        prev.TenantIds.push(act);
        return prev;
      },
      { TenantIds: [] as string[] }
    );

    return {
      UserQuery,
      ...searchParamsJSON,
      ...parsedCountryParams,
      ...parsedTenantParam,
    };
  }, [searchParams]);

  const skipSearchRequest = useMemo(() => {
    return !Object.values(SearchCandidateFilterTypes).find((singleType) =>
      searchParams.get(singleType)
    );
  }, [searchParams]);

  /* Search used for the general page */
  const {
    data: SearchCandidateData,
    isLoading: isLoadingSearchCandidate,
    isFetching: isFetchingSearchCandidate,
  } = useSearchCandidateQuery(
    {
      ...searchCandidatesFilters,
      orderBy: columnSort.column,
      Order: columnSort.sortType,
      page,
      take: pageSize,
    } as SearchCandidateInput,
    {
      skip: skipSearchRequest,
    }
  );

  /* Search used for the export functionality */

  useEffect(() => {
    const newParams = new URLSearchParams(searchParams);

    const searchedKeyword = newParams.get('Keyword') || '';
    // First time searching, save keywords
    if (!lastSearch.current) {
      lastSearch.current = searchedKeyword;
    }

    // If keywords has changed, save new keywords and reset to page 1
    if (lastSearch.current && lastSearch.current !== searchedKeyword) {
      lastSearch.current = searchedKeyword;
      setPage(1);
    }
    newParams.set('take', pageSize.toString());
    newParams.set('page', page.toString());
    // Guard: only push to the URL when the values actually changed, otherwise
    // setSearchParams would produce a new searchParams reference on every render
    // and re-trigger this effect indefinitely (stack overflow).
    if (
      searchParams.get('take') !== pageSize.toString() ||
      searchParams.get('page') !== page.toString()
    ) {
      setSearchParams(newParams);
    }
  }, [page, pageSize, searchParams, setSearchParams]);

  /** Cleans selections after each load */
  useEffect(() => {
    if (isLoadingSearchCandidate || isFetchingSearchCandidate)
      setRowSelection({});
  }, [isFetchingSearchCandidate, isLoadingSearchCandidate]);
  return (
    <div className="flex flex-row mt-2 w-full">
      <ModalInTailwind
        isOpen={isGeneratingCSV}
        closeModal={() => setIsGeneratingCSV(false)}
      >
        <div className="bg-white rounded p-8">
          <p className="text-xl mb-4">Generating CSV... Please wait</p>
          <Spinner height="100%" width="100%" />
        </div>
      </ModalInTailwind>
      {/* Modal to add selected candidates into a position */}
      <SearchCandidateAddToPositionModal
        isOpen={openAddToPosition}
        onCloseHandler={() => setOpenAddToPosition(false)}
        selectedCandidates={rowSelection}
        searchCandidateData={SearchCandidateData?.data || []}
        setSelectedCandidates={setRowSelection}
      />
      <SearchCandidatesFilter />
      <div className="ml-4 pr-6 flex flex-col flex-1">
        <div className="flex flex-row justify-between mt-2">
          <h1 className="text-4xl text-gray-500 mb-4">Search Candidates</h1>
          <button
            type="button"
            onClick={() => setOpenAddToPosition(true)}
            className="bg-internal-main rounded text-white font-semibold px-4 "
          >
            <i className="pi pi-plus mr-2" />
            Add to position
          </button>
        </div>
        <div className="mb-4">
          <MultiFilterChipsInput
            title="Keyword"
            filterOptions={filterOptions}
          />
        </div>
        <SearchCandidatesTable
          searchCandidateData={SearchCandidateData?.data || []}
          isLoading={isLoadingSearchCandidate || isFetchingSearchCandidate}
          selectedRows={rowSelection}
          setSelectedRows={setRowSelection}
          pageSize={pageSize}
          columnSort={columnSort}
          setColumnSort={setColumnSort}
          // openAddToPositionModal={() => setOpenAddToPosition(true)}
        />
        {(isLoadingSearchCandidate || isFetchingSearchCandidate) && (
          <div className="w-full overflow-hidden">
            <Spinner secondary width="100%" height="200px" />
          </div>
        )}
        <SearchCandidatesTableNavigation
          searchCandidateData={SearchCandidateData}
          pageSize={pageSize}
          setPageSize={setPageSize}
          selectedRows={rowSelection}
          columnSort={columnSort}
          setIsGeneratingCSV={setIsGeneratingCSV}
          page={page}
          setPage={setPage}
          searchCandidatesFilters={searchCandidatesFilters}
          openAddPositionHandler={() => setOpenAddToPosition(true)}
        />
      </div>
    </div>
  );
};

export default SearchCandidates;
